import useAnalytics from 'core/hooks/useAnalytics';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { EVENT_NAMES } from '../../constants/eventNames';
import { getTeacherTools } from './teacherToolsConfig';

/**
 * useTeacherToolsView - State, handlers and analytics for the AI teacher tools screen.
 */

export type TeacherTool = {
  id: string;
  title: string;
  description: string;
  icon?: string;
  iconColor?: string;
  category?: string;
  available?: boolean;
  opensDialog?: string;
};

type UseTeacherToolsViewReturn = {
  isQuizDialogOpen: boolean;
  onCloseQuizDialog: () => void;
  onShowMobileMenu: () => void;
  onToolClick: (tool: TeacherTool) => void;
  shouldShowMobileMenu: boolean;
  t: (key: string) => string;
  tools: TeacherTool[];
};

function useTeacherToolsView(): UseTeacherToolsViewReturn {
  const { t } = useTranslation(['chatbot']);
  const history = useHistory();
  const { trackEvent } = useAnalytics();
  const [shouldShowMobileMenu, setShouldShowMobileMenu] = useState<boolean>(false);
  const [isQuizDialogOpen, setIsQuizDialogOpen] = useState<boolean>(false);
  const hasTrackedOpenedRef = useRef<boolean>(false);

  useEffect(() => {
    if (hasTrackedOpenedRef.current) return;
    hasTrackedOpenedRef.current = true;
    trackEvent(EVENT_NAMES.IA_TEACHER_TOOLS_OPENED, {}).catch((error: unknown) => console.error('Analytics tracking failed:', error));
  }, [trackEvent]);

  const tools: TeacherTool[] = getTeacherTools(t);

  function onShowMobileMenu(): void {
    setShouldShowMobileMenu(!shouldShowMobileMenu);
    if (!shouldShowMobileMenu) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = 'scroll';
    }
  }

  function onToolClick(tool: TeacherTool): void {
    trackEvent(EVENT_NAMES.IA_TEACHER_TOOL_CLICKED, { tool_type: tool.id }).catch((error: unknown) => console.error('Analytics tracking failed:', error));
    if (tool.opensDialog === 'QuizLocationDialog') {
      setIsQuizDialogOpen(true);
    } else {
      history.push(`/teacher-tools/setup/${tool.id}`);
    }
  }

  function onCloseQuizDialog(): void {
    setIsQuizDialogOpen(false);
  }

  return { isQuizDialogOpen, onCloseQuizDialog, onShowMobileMenu, onToolClick, shouldShowMobileMenu, t, tools };
}

export default useTeacherToolsView;
